import { useState, useEffect } from 'react'
import { apiService } from '../services/api'

const DEALERSHIP_CONFIG_KEY = 'ambcar-dealership-config'

function getAllowedCategories(): string[] {
  try {
    const raw = localStorage.getItem(DEALERSHIP_CONFIG_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed?.allowedCategories) ? parsed.allowedCategories : []
  } catch {
    return []
  }
}

export interface VehicleOption {
 value: string
 label: string
}

export interface VehicleColor {
 value: string
 label: string
 hex?: string
}

export interface DoorOption {
 value: number
 label: string
}

/** CNH-style vehicle category codes from API (A=moto, B=carro, C=caminhão, D=ônibus) */
export const VEHICLE_CATEGORY_CODES = ['A', 'B', 'C', 'D'] as const
export type VehicleCategoryCode = (typeof VEHICLE_CATEGORY_CODES)[number]

/** Feature option groups for one vehicle category (from `/vehicles/options/all` → `features`) */
export interface VehicleFeatureGroups {
 comfort: VehicleOption[]
 safety: VehicleOption[]
 technology: VehicleOption[]
 convenience: VehicleOption[]
 performance: VehicleOption[]
 lighting: VehicleOption[]
 adas: VehicleOption[]
}

export function emptyVehicleFeatureGroups(): VehicleFeatureGroups {
 return {
 comfort: [],
 safety: [],
 technology: [],
 convenience: [],
 performance: [],
 lighting: [],
 adas: []
 }
}

function normalizeVehicleFeatureGroups(raw: Partial<VehicleFeatureGroups> | undefined | null): VehicleFeatureGroups {
 const e = emptyVehicleFeatureGroups()
 if (!raw || typeof raw !== 'object') return e
 return {
 comfort: Array.isArray(raw.comfort) ? raw.comfort : e.comfort,
 safety: Array.isArray(raw.safety) ? raw.safety : e.safety,
 technology: Array.isArray(raw.technology) ? raw.technology : e.technology,
 convenience: Array.isArray(raw.convenience) ? raw.convenience : e.convenience,
 performance: Array.isArray(raw.performance) ? raw.performance : e.performance,
 lighting: Array.isArray(raw.lighting) ? raw.lighting : e.lighting,
 adas: Array.isArray(raw.adas) ? raw.adas : e.adas
 }
}

/**
 * Maps API `features` (nested by A–D, or legacy flat object) to `featuresByCategory`.
 */
export function mapFeaturesByCategoryFromApi(apiFeatures: any): Record<VehicleCategoryCode, VehicleFeatureGroups> {
 if (!apiFeatures || typeof apiFeatures !== 'object') {
 return {
 A: emptyVehicleFeatureGroups(),
 B: emptyVehicleFeatureGroups(),
 C: emptyVehicleFeatureGroups(),
 D: emptyVehicleFeatureGroups()
 }
 }
 const hasNestedCodes = VEHICLE_CATEGORY_CODES.some(code => apiFeatures[code] !== undefined)
 if (!hasNestedCodes && Array.isArray(apiFeatures.comfort)) {
 return {
 A: emptyVehicleFeatureGroups(),
 B: normalizeVehicleFeatureGroups(apiFeatures),
 C: emptyVehicleFeatureGroups(),
 D: emptyVehicleFeatureGroups()
 }
 }
 return {
 A: normalizeVehicleFeatureGroups(apiFeatures.A),
 B: normalizeVehicleFeatureGroups(apiFeatures.B),
 C: normalizeVehicleFeatureGroups(apiFeatures.C),
 D: normalizeVehicleFeatureGroups(apiFeatures.D)
 }
}

export interface VehicleOptions {
 categories: VehicleOption[]
 types: VehicleOption[]
 bodyTypes: VehicleOption[] // Tipos de carroceria
 fuelTypes: VehicleOption[]
 transmissions: VehicleOption[]
 conditions: VehicleOption[]
 statuses: VehicleOption[]
 armorLevels: VehicleOption[]
 specialCategories: VehicleOption[]
 steeringTypes: VehicleOption[]
 characteristics: VehicleOption[]
 segmentTypes: VehicleOption[]
 /** Equipamentos/opcionais por categoria de veículo (A–D), alinhado ao backend */
 featuresByCategory: Record<VehicleCategoryCode, VehicleFeatureGroups>
 colors: VehicleColor[]
 doors: DoorOption[]
 
 // Leilões
 auctions: {
 categories: VehicleOption[]
 damageLevels: VehicleOption[]
 insurance: VehicleOption[]
 bank: VehicleOption[]
 judicial: VehicleOption[]
 federal: VehicleOption[]
 police: VehicleOption[]
 fleet: VehicleOption[]
 private: VehicleOption[]
 }
 
 // Motos
 motorcycles: {
 engineTypes: VehicleOption[]
 coolingTypes: VehicleOption[]
 starterTypes: VehicleOption[]
 brakeTypes: VehicleOption[]
 feedingTypes: VehicleOption[]
 styles: VehicleOption[]
 categories: VehicleOption[]
 cylinderCapacities: VehicleOption[]
 }

 // Caminhões
 trucks: {
 bodyTypes: VehicleOption[]
 cabTypes: VehicleOption[]
 tractionTypes: VehicleOption[]
 }

 // Ônibus
 buses: {
 bodyBrands: VehicleOption[]
 operationTypes: VehicleOption[]
 equipment: VehicleOption[]
 }
}

const DEFAULT_FEATURE_CATEGORY_FALLBACK: VehicleCategoryCode = 'B'

/**
 * Resolved feature option lists for the given category (e.g. form `category` or `vehicle.basic.category`).
 */
export function getVehicleFeatureGroupsForCategory(
 options: Pick<VehicleOptions, 'featuresByCategory'> | null | undefined,
 categoryCode: string | undefined | null
): VehicleFeatureGroups {
 if (!options?.featuresByCategory) return emptyVehicleFeatureGroups()
 const code =
 categoryCode && VEHICLE_CATEGORY_CODES.includes(categoryCode as VehicleCategoryCode)
 ? (categoryCode as VehicleCategoryCode)
 : DEFAULT_FEATURE_CATEGORY_FALLBACK
 return options.featuresByCategory[code] ?? emptyVehicleFeatureGroups()
}

export const useVehicleOptions = () => {
 const [options, setOptions] = useState<VehicleOptions>({
 categories: [],
 types: [],
 bodyTypes: [],
 fuelTypes: [],
 transmissions: [],
 conditions: [],
 statuses: [],
 armorLevels: [],
 specialCategories: [],
 steeringTypes: [],
 characteristics: [],
 segmentTypes: [],
 featuresByCategory: {
 A: emptyVehicleFeatureGroups(),
 B: emptyVehicleFeatureGroups(),
 C: emptyVehicleFeatureGroups(),
 D: emptyVehicleFeatureGroups()
 },
 colors: [],
 doors: [],
 
 // Leilões
 auctions: {
 categories: [],
 damageLevels: [],
 insurance: [],
 bank: [],
 judicial: [],
 federal: [],
 police: [],
 fleet: [],
 private: []
 },
 
 // Motos
 motorcycles: {
 engineTypes: [],
 coolingTypes: [],
 starterTypes: [],
 brakeTypes: [],
 feedingTypes: [],
 styles: [],
 categories: [],
 cylinderCapacities: []
 },

 // Caminhões
 trucks: {
 bodyTypes: [],
 cabTypes: [],
 tractionTypes: []
 },

 // Ônibus
 buses: {
 bodyBrands: [],
 operationTypes: [],
 equipment: []
 }
 })
 
 const [isLoading, setIsLoading] = useState(false)
 const [error, setError] = useState<string | null>(null)

 const fetchAllOptions = async () => {
 try {
 setIsLoading(true)
 setError(null)
 console.log('🔄 Buscando todas as opções de veículos...')
 const response = await apiService.getAllVehicleOptions()
 console.log('✅ Opções carregadas:', response)
 console.log('📊 Detalhes das opções:')
 console.log('- Categorias:', response.categories?.length || 0)
 console.log('- Tipos:', response.types?.length || 0)
 console.log('- Combustíveis:', response.fuelTypes?.length || 0)
 console.log('- Transmissões:', response.transmissions?.length || 0)
 console.log('- Condições:', response.conditions?.length || 0)
 console.log('- Status:', response.statuses?.length || 0)
 console.log('- Blindagem:', response.armorLevels?.length || 0)
 console.log('- Categorias especiais:', response.specialCategories?.length || 0)
 console.log('- Direções:', response.steeringTypes?.length || 0)
 console.log('- Características:', response.characteristics?.length || 0)
 console.log('- Cores:', response.colors?.length || 0)
 console.log('- Portas:', response.doors?.length || 0)
 console.log('- Leilões - Categorias:', response.auctions?.categories?.length || 0)
 console.log('- Leilões - Níveis de Avaria:', (response as any).damageLevels?.length || 0)
 console.log('- Leilões - Seguros:', response.auctions?.insurance?.length || 0)
 console.log('- Leilões - Bancos:', response.auctions?.bank?.length || 0)
 console.log('- Leilões - Judicial:', response.auctions?.judicial?.length || 0)
 console.log('- Leilões - Federal:', response.auctions?.federal?.length || 0)
 console.log('- Leilões - Polícia:', response.auctions?.police?.length || 0)
 console.log('- Leilões - Frota:', response.auctions?.fleet?.length || 0)
 console.log('- Leilões - Privado:', response.auctions?.private?.length || 0)
 console.log('- Motos - Tipos de motor:', response.motorcycles?.engineTypes?.length || 0)
 console.log('- Motos - Tipos de refrigeração:', response.motorcycles?.coolingTypes?.length || 0)
 console.log('- Motos - Tipos de partida:', response.motorcycles?.starterTypes?.length || 0)
 console.log('- Motos - Tipos de freio:', response.motorcycles?.brakeTypes?.length || 0)
 console.log('- Motos - Tipos de alimentação:', response.motorcycles?.feedingTypes?.length || 0)
 console.log('- Motos - Estilos:', response.motorcycles?.styles?.length || 0)
 console.log('- Motos - Categorias:', response.motorcycles?.categories?.length || 0)
 console.log('- Motos - Capacidades cilíndricas:', response.motorcycles?.cylinderCapacities?.length || 0)
 
 // Mapeia a resposta para a estrutura esperada
 const apiResponse = response as any
 const mappedOptions = {
 categories: response.categories || [],
 types: response.types || [],
 bodyTypes: response.types || [], // Usa 'types' como bodyTypes
 fuelTypes: response.fuelTypes || [],
 transmissions: response.transmissions || [],
 conditions: response.conditions || [],
 statuses: response.statuses || [],
 armorLevels: response.armorLevels || [],
 specialCategories: response.specialCategories || [],
 steeringTypes: response.steeringTypes || [],
 characteristics: response.characteristics || [],
 segmentTypes: apiResponse.segmentTypes || [],
 featuresByCategory: mapFeaturesByCategoryFromApi(apiResponse.features),
 colors: response.colors || [],
 doors: response.doors || [],
 
 // Leilões - damageLevels vem dentro de auctions
 auctions: {
 categories: response.auctions?.categories || [],
 damageLevels: (response.auctions as any)?.damageLevels || [],
 insurance: response.auctions?.insurance || [],
 bank: response.auctions?.bank || [],
 judicial: response.auctions?.judicial || [],
 federal: response.auctions?.federal || [],
 police: response.auctions?.police || [],
 fleet: response.auctions?.fleet || [],
 private: response.auctions?.private || []
 },
 
 // Motos
 motorcycles: {
 engineTypes: response.motorcycles?.engineTypes || [],
 coolingTypes: response.motorcycles?.coolingTypes || [],
 starterTypes: response.motorcycles?.starterTypes || [],
 brakeTypes: response.motorcycles?.brakeTypes || [],
 feedingTypes: response.motorcycles?.feedingTypes || [],
 styles: response.motorcycles?.styles || [],
 categories: response.motorcycles?.categories || [],
 cylinderCapacities: response.motorcycles?.cylinderCapacities || []
 },

 // Caminhões
 trucks: {
 bodyTypes: (response as any).trucks?.bodyTypes || [],
 cabTypes: (response as any).trucks?.cabTypes || [],
 tractionTypes: (response as any).trucks?.tractionTypes || []
 },

 // Ônibus
 buses: {
 bodyBrands: (response as any).buses?.bodyBrands || [],
 operationTypes: (response as any).buses?.operationTypes || [],
 equipment: (response as any).buses?.equipment || []
 }
 }
 
 // Filter categories based on dealership config
 const allowedCategories = getAllowedCategories()
 if (allowedCategories.length > 0) {
   mappedOptions.categories = mappedOptions.categories.filter(
     c => allowedCategories.includes(c.value)
   )
 }

 setOptions(mappedOptions)
 } catch (err) {
 console.error('❌ Erro ao buscar opções:', err)
 setError('Erro ao carregar opções de veículos')
 } finally {
 setIsLoading(false)
 }
 }

 // Carrega todas as opções automaticamente quando o hook é inicializado
 useEffect(() => {
 fetchAllOptions()
 }, [])

 return {
 options,
 isLoading,
 error,
 fetchAllOptions
 }
}

// Hook específico para opções de leilões
export const useAuctionOptions = () => {
 const [options, setOptions] = useState({
 categories: [] as VehicleOption[],
 insurance: [] as VehicleOption[],
 bank: [] as VehicleOption[],
 judicial: [] as VehicleOption[],
 federal: [] as VehicleOption[],
 police: [] as VehicleOption[],
 fleet: [] as VehicleOption[],
 private: [] as VehicleOption[]
 })
 
 const [isLoading, setIsLoading] = useState(false)
 const [error, setError] = useState<string | null>(null)

 const fetchAllAuctionOptions = async () => {
 try {
 setIsLoading(true)
 setError(null)
 console.log('🔄 Buscando opções de leilões...')
 const response = await apiService.getAllAuctionOptions()
 console.log('✅ Opções de leilões carregadas:', response)
 setOptions(response)
 } catch (err) {
 console.error('❌ Erro ao buscar opções de leilões:', err)
 setError('Erro ao carregar opções de leilões')
 } finally {
 setIsLoading(false)
 }
 }

 useEffect(() => {
 fetchAllAuctionOptions()
 }, [])

 return {
 options,
 isLoading,
 error,
 fetchAllAuctionOptions
 }
}

// Hook específico para opções de motos
export const useMotorcycleOptions = () => {
 const [options, setOptions] = useState({
 engineTypes: [] as VehicleOption[],
 coolingTypes: [] as VehicleOption[],
 starterTypes: [] as VehicleOption[],
 brakeTypes: [] as VehicleOption[],
 feedingTypes: [] as VehicleOption[],
 styles: [] as VehicleOption[],
 categories: [] as VehicleOption[],
 cylinderCapacities: [] as VehicleOption[]
 })
 
 const [isLoading, setIsLoading] = useState(false)
 const [error, setError] = useState<string | null>(null)

 const fetchAllMotorcycleOptions = async () => {
 try {
 setIsLoading(true)
 setError(null)
 console.log('🔄 Buscando opções de motos...')
 const response = await apiService.getAllMotorcycleOptions()
 console.log('✅ Opções de motos carregadas:', response)
 setOptions(response)
 } catch (err) {
 console.error('❌ Erro ao buscar opções de motos:', err)
 setError('Erro ao carregar opções de motos')
 } finally {
 setIsLoading(false)
 }
 }

 useEffect(() => {
 fetchAllMotorcycleOptions()
 }, [])

 return {
 options,
 isLoading,
 error,
 fetchAllMotorcycleOptions
 }
}

